import { useState } from 'react';
import {
  DSButton,
  DSDrawer,
  DSIcon,
  DSRadio,
  DSTypography,
  DsAvatar,
  DsHeader,
  DsUserDropdown
} from '@eiv2/design-system';
import { useAuth } from '../auth/useAuth';

export interface HeaderProps {
  onOpenSidebar: () => void;
}

function displayNameFromEmail(email: string): string {
  const local = email.split('@')[0]?.trim() ?? '';
  if (!local) return email;

  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Card de conta no estilo Figma (selecionado: borda/fundo brand + rádio). */
function AccountSwitcherItem({
  email,
  userName,
  selected = true
}: {
  email: string;
  userName: string;
  selected?: boolean;
}) {
  return (
    <div
      aria-current={selected ? 'true' : undefined}
      className={[
        'flex w-full items-center gap-2 rounded-[12px] border border-solid p-3',
        selected
          ? 'border-brand-primary-3 bg-brand-primary-3/10'
          : 'border-gray-2 bg-dark-light-mode-background'
      ].join(' ')}
    >
      <DsAvatar
        className="bg-brand-primary-3 text-body-light-mode-1"
        name={userName}
        size={40}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <DSTypography as="p" className="truncate text-[14px] font-semibold text-dark-3">
          {userName}
        </DSTypography>
        {email ? (
          <DSTypography as="p" className="truncate text-[12px] font-normal text-gray-5">
            {email}
          </DSTypography>
        ) : null}
      </div>
      <DSRadio
        aria-label={selected ? 'Conta selecionada' : `Selecionar ${userName}`}
        checked={selected}
        className="pointer-events-none"
        readOnly
        tabIndex={-1}
      />
    </div>
  );
}

function AccountMenuLink({
  danger,
  icon,
  label,
  onClick
}: {
  danger?: boolean;
  icon: 'User' | 'Login';
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className={[
        'flex w-full cursor-pointer items-center gap-2 rounded-[4px] p-3 text-left text-[14px] font-normal transition-colors hover:bg-gray-2',
        danger ? 'text-feedback-danger' : 'text-dark-3'
      ].join(' ')}
      onClick={onClick}
      role="menuitem"
      type="button"
    >
      <DSIcon name={icon} size={16} />
      {label}
    </button>
  );
}

function AccountMenuContent({
  email,
  userName,
  onLogout,
  titleId
}: {
  email: string;
  userName: string;
  onLogout: () => void;
  titleId?: string;
}) {
  return (
    <div className="flex w-full flex-col gap-4">
      <DSTypography
        as="h2"
        className="text-[16px] font-semibold text-dark-3"
        id={titleId}
      >
        Alternar conta
      </DSTypography>

      <div className="flex max-h-[356px] flex-col gap-4 overflow-y-auto">
        <AccountSwitcherItem email={email} selected userName={userName} />
      </div>

      <AccountMenuLink
        icon="Login"
        label="Sair"
        danger
        onClick={onLogout}
      />
    </div>
  );
}

export function Header({ onOpenSidebar }: HeaderProps) {
  const { admin, logout } = useAuth();
  const email = admin?.email ?? '';
  const userName = email ? displayNameFromEmail(email) : 'Administrador';
  const [userSheetOpen, setUserSheetOpen] = useState(false);

  return (
    <>
      <DsHeader
        endContent={
          <div className="flex items-center gap-4">
            <button
              aria-label="Abrir menu"
              className="inline-flex size-6 cursor-pointer items-center justify-center text-dark-3 md:hidden"
              onClick={onOpenSidebar}
              type="button"
            >
              <DSIcon name="HamburgerMenu" size={20} />
            </button>

            <div className="hidden md:block">
              <DsUserDropdown userName={userName}>
                <AccountMenuContent
                  email={email}
                  onLogout={() => {
                    void logout();
                  }}
                  userName={userName}
                />
              </DsUserDropdown>
            </div>

            <button
              aria-expanded={userSheetOpen}
              aria-label="Conta do usuário"
              className="inline-flex cursor-pointer items-center md:hidden"
              onClick={() => setUserSheetOpen(true)}
              type="button"
            >
              <DsAvatar
                className={
                  userSheetOpen
                    ? 'bg-brand-primary-3 text-body-light-mode-1'
                    : 'bg-dark-light-mode-background text-brand-primary-3'
                }
                name={userName}
                size={40}
              />
            </button>
          </div>
        }
      />

      <DSDrawer
        aria-labelledby="user-sheet-title"
        className="flex max-h-[calc(100dvh-40px)] w-full flex-col overflow-hidden rounded-t-[24px] rounded-b-none"
        onClose={() => setUserSheetOpen(false)}
        open={userSheetOpen}
        orientation="bottom"
        overlayClassName="bg-overlay/45"
        size="fit"
      >
        <div className="flex h-6 shrink-0 items-center justify-center" aria-hidden>
          <span className="h-1 w-10 rounded-full bg-gray-3" />
        </div>

        <div className="flex flex-col gap-4 p-6">
          <AccountMenuContent
            email={email}
            onLogout={() => {
              setUserSheetOpen(false);
              void logout();
            }}
            titleId="user-sheet-title"
            userName={userName}
          />

          <DSButton
            className="w-full"
            onClick={() => setUserSheetOpen(false)}
            type="button"
            variant="secondary"
          >
            Fechar
          </DSButton>
        </div>
      </DSDrawer>
    </>
  );
}
